import type { KonvaEventObject } from 'konva/lib/Node';
import React, { useRef } from 'react';
import { Group, Line, Ellipse } from 'react-konva';
import type { CylinderType } from 'src/types/transforms';

import { calculatePosition } from 'src/util/math';
import { useDragGesture } from 'src/hooks/useDragGesture';

interface CylinderBorderProps {
    scaledCylinderWarp: Omit<CylinderType, 'id' | 'fullWrap'>;
    cylinderWarp: CylinderType;
    selected: boolean;
    scale: number;
    selectLayer: () => void;
    onDragMove: (cylinderWarp: CylinderType) => void;
    onDragEnd: () => void;
}

const CylinderBorder: React.FC<CylinderBorderProps> = (props) => {
    const {
        scaledCylinderWarp,
        cylinderWarp,
        selected,
        scale,
        selectLayer,
        onDragMove: onDragMoveProp,
        onDragEnd,
    } = props;

    const startCylinderWarpRef = useRef<CylinderType | null>(null);
    const clientXRef = useRef<number | null>(null);
    const clientYRef = useRef<number | null>(null);

    const handleDragMove = (e: MouseEvent) => {
        const deltaX = (clientXRef.current! - e.clientX) / scale;
        const deltaY = (clientYRef.current! - e.clientY) / scale;
        const newCylinderWarp: CylinderType = JSON.parse(JSON.stringify(startCylinderWarpRef.current));
        newCylinderWarp.cylinderTop.center.x -= deltaX;
        newCylinderWarp.cylinderBottom.center.x -= deltaX;
        newCylinderWarp.cylinderTop.center.y -= deltaY;
        newCylinderWarp.cylinderBottom.center.y -= deltaY;

        onDragMoveProp(newCylinderWarp);
    };

    const { start } = useDragGesture({ onDragMove: handleDragMove, onDragEnd });

    const onDragStart = (e: KonvaEventObject<globalThis.MouseEvent>) => {
        startCylinderWarpRef.current = cylinderWarp;
        clientXRef.current = e.evt.clientX;
        clientYRef.current = e.evt.clientY;

        start();
    };

    const onMouseDown = (e: KonvaEventObject<globalThis.MouseEvent>) => {
        e.cancelBubble = true;
        if (selected) {
            onDragStart(e);
        } else {
            selectLayer();
        }
    };

    const topLeftPosition = calculatePosition(scaledCylinderWarp.cylinderTop, -180);
    const bottomLeftPosition = calculatePosition(scaledCylinderWarp.cylinderBottom, -180);
    const leftLine = [topLeftPosition.x, topLeftPosition.y, bottomLeftPosition.x, bottomLeftPosition.y];

    const topRightPosition = calculatePosition(scaledCylinderWarp.cylinderTop, 0);
    const bottomRightPosition = calculatePosition(scaledCylinderWarp.cylinderBottom, 0);
    const rightLine = [topRightPosition.x, topRightPosition.y, bottomRightPosition.x, bottomRightPosition.y];

    const draggableBox = [topLeftPosition.x, topLeftPosition.y]
        .concat(rightLine)
        .concat([bottomLeftPosition.x, bottomLeftPosition.y]);

    const color = selected ? 'red' : 'transparent';

    return (
        <Group>
            <Line points={leftLine} stroke={color} strokeWidth={1} />
            <Line points={rightLine} stroke={color} strokeWidth={1} />
            <Line points={draggableBox} stroke='transparent' strokeWidth={1} closed={true} onMouseDown={onMouseDown} />
            <Ellipse
                x={scaledCylinderWarp.cylinderTop.center.x}
                y={scaledCylinderWarp.cylinderTop.center.y}
                radiusX={scaledCylinderWarp.cylinderTop.radiusX}
                radiusY={scaledCylinderWarp.cylinderTop.radiusY}
                rotation={scaledCylinderWarp.cylinderTop.rotationDegrees}
                stroke={color}
                strokeWidth={1}
                onMouseDown={onMouseDown}
            />
            <Ellipse
                x={scaledCylinderWarp.cylinderBottom.center.x}
                y={scaledCylinderWarp.cylinderBottom.center.y}
                radiusX={scaledCylinderWarp.cylinderBottom.radiusX}
                radiusY={scaledCylinderWarp.cylinderBottom.radiusY}
                rotation={scaledCylinderWarp.cylinderBottom.rotationDegrees}
                stroke={color}
                strokeWidth={1}
                onMouseDown={onMouseDown}
            />
        </Group>
    );
};

export default CylinderBorder;
